/**
 * 매장 팔로우 토글 — 노출 판정 + 낙관적 반영 + 응답 갈래 처리 (admin#2496 / #885).
 *
 * 전역 동의(연락처 단위)는 그대로 두고 **어느 매장의 광고를 받을지**만 고른다.
 * 언팔로우는 그 매장 광고만 끊고 순번·호출 안내는 그대로 둔다(follow 설계 §1.1).
 * 철회 링크와 같은 화면에 서지만 축이 다르므로 훅도 따로 둔다 — 철회는 연락처 전역·
 * 비가역, 팔로우는 매장 하나·가역이다.
 *
 * ## 로컬 저장을 하지 않는다
 * 팔로우는 (연락처 × 매장) 상태라 단말에 캐시하면 다른 기기·다른 브라우저와 어긋난다.
 * 서버가 SSOT 이고 이 훅은 서버 값 위에 **진행 중 낙관값만** 잠시 덮는다(follow 설계 §4.5).
 *
 * ## `store_followed` 가 없으면 토글을 감춘다
 * `current` 만 아는 서버(admin#2493 선배포분)가 실재한다. 그 응답에서 팔로우 여부를
 * false 로 접으면 **팔로우 중인 고객에게 "이 매장 프로모션을 받지 않습니다"** 라고
 * 말하게 되고, 끄려 눌러도 엔드포인트가 없어 실패한다. 모르면 조작면을 내보내지
 * 않는 편이 정직하다 — 기존 철회 링크는 그대로 뜬다.
 *
 * ## 실패는 되돌린다
 * 토글은 확인 다이얼로그 없이 즉시 반영한다(가역이므로). 그래서 요청이 실패하면
 * 화면이 서버와 어긋난 채 남지 않도록 **누르기 직전 값으로 되돌린다**. 되돌릴 목표는
 * "서버 값"이 아니라 "직전 화면 값"이다 — 폴링이 아직 이전 토글을 반영하지 못했을 때
 * 서버 값으로 되돌리면 성공했던 조작까지 뒤집힌다.
 *
 * ## 덮개는 "다음 서버 응답이 올 때"까지만 산다
 * 되돌린 값도, 서버가 확정해 준 값도 **요청이 끝난 뒤 처음 도착하는 상태 응답**에
 * 자리를 내준다(값이 같든 다르든). "서버가 낙관값을 따라잡으면 걷는다"로 조건을 걸면
 * 커밋 뒤 타임아웃처럼 **요청은 실패했는데 서버에는 적용된** 경우 두 값이 영원히
 * 어긋나 덮개가 걷히지 않는다 — 화면은 반대 상태를 보여주면서 "저장하지 못했어요"까지
 * 띄운다(실제로는 저장됨).
 *
 * ## 그래서 settle 직후 상태를 한 번 다시 읽는다(`onSettled`)
 * 위 해제 조건은 "다음 상태 응답이 온다"를 전제하는데, 종결 화면(seated/cancelled)은
 * 폴링이 아예 멈춰 있다(useWaitlistParty 의 종결 판정). 그 화면에서 커밋 뒤 응답이
 * 유실되면 **도착할 응답이 영영 없어** 덮개가 굳는다 — 화면은 롤백값과 오류 문구를 단
 * 채 서버의 반대 값과 영구히 어긋난다. 그래서 성공·실패를 가리지 않고 settle 직후
 * 호출 측에 재조회를 요청한다(전역 철회 경로가 FilaVirtualPage 에서 쓰는 처치와 동일).
 * 재조회가 실어 온 값이 시도값과 같으면 그 요청은 실제로 저장된 것이므로 "저장하지
 * 못했어요"도 함께 거둔다 — 남겨 두면 고객이 재시도로 방금 저장된 설정을 되돌린다.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  isWaitlistStoreFollowNotApplicableError,
  isWaitlistStoreFollowReconsentRequiredError,
  isWaitlistStoreFollowUnavailableError,
  waitlistApi,
} from '@/lib/api/waitlist';
import type { WaitlistPartyMarketingConsentState } from '@/lib/api/waitlist';
import { logger } from '@/utils/logger';

/**
 * 진행 상태.
 *
 * `reconsent` 는 409 `STORE_FOLLOW_RECONSENT_REQUIRED` 전용이다 — 재시도해도 결과가
 * 같고(사다리 상위 억제가 살아 있다) 해제 경로가 등록 화면의 새 동의뿐이라, "다시
 * 시도해 주세요"로 뭉개면 고객이 켤 방법을 영영 못 찾는다.
 */
type StoreFollowPhase = 'idle' | 'pending' | 'failed' | 'reconsent';

export interface UseStoreMarketingFollowResult {
  /** 토글 블록을 렌더할지. */
  visible: boolean;
  /** 스위치의 현재 값(`aria-checked`) — 진행 중에는 낙관값이다. */
  followed: boolean;
  /** 요청 진행 중 — 컨트롤을 비활성화해 중복 제출을 막는다. */
  pending: boolean;
  /** 재시도로 풀릴 수 있는 실패(네트워크·5xx·409 `STORE_FOLLOW_UNAVAILABLE`). */
  failed: boolean;
  /** 409 `STORE_FOLLOW_RECONSENT_REQUIRED` — 등록 화면의 새 동의로만 풀린다. */
  reconsentRequired: boolean;
  toggle: () => Promise<void>;
}

/**
 * @param accessToken 토글 자격증명(파티 토큰). 없으면 호출 자체가 불가하다.
 * @param enabled  토큰이 유효한 상태 화면인가(등록 화면에서는 false).
 * @param consent  상태 응답의 `marketing_consent` 객체 **그대로**(가공·재생성 금지).
 *        불리언을 풀어 받지 않는 이유는 {@link WaitlistPartyMarketingConsentState}
 *        주석 참고. 이 훅은 **객체가 바뀌는 것을 "새 상태 응답이 도착했다"는 신호**로
 *        쓴다(아래 덮개 해제). 매 렌더 새로 만들어 넘기면 폴링이 매번 도착한 것으로
 *        오인돼 낙관 덮개가 즉시 걷힌다.
 * @param onSettled 토글 요청이 끝난(성공·실패 무관) 직후 **한 번** 불린다. 호출 측은
 *        여기서 상태를 다시 읽어 `consent` 를 새로 공급한다 — 폴링이 멈춘 종결 화면에서
 *        낙관 덮개를 걷을 유일한 기회다(위 주석). 재조회를 실패해도 화면은 지금과 같은
 *        상태로 남을 뿐이므로 결과는 보지 않는다.
 */
export function useStoreMarketingFollow(
  accessToken: string | null,
  enabled: boolean,
  consent?: WaitlistPartyMarketingConsentState,
  onSettled?: () => void,
): UseStoreMarketingFollowResult {
  const serverCurrent = consent?.current;
  const serverFollowed = consent?.store_followed;

  /** 서버 값 위에 덮는 낙관값. null 이면 서버가 권위다. */
  const [optimistic, setOptimistic] = useState<boolean | null>(null);
  /**
   * 토글 응답이 함께 준 `current` — 폴링 값보다 **우선**한다.
   *
   * 서버는 전역 억제가 서 있어도 토글 자체는 허용하고(선호는 기록한다, follow 설계
   * §3.2) 응답의 `current:false` 로 "지금은 어차피 안 나간다"를 알려 준다. 그 값을
   * 버리면 화면은 방금 서버에게 들은 사실을 무시한 채 토글 ON 과 "이 번호로 쿠폰을
   * 받습니다"를 유지한다 — 종결 화면(seated/cancelled)은 폴링이 멈춰 영영 그대로다.
   */
  const [responseCurrent, setResponseCurrent] = useState<boolean | null>(null);
  const [phase, setPhase] = useState<StoreFollowPhase>('idle');
  /**
   * 404 `STORE_FOLLOW_NOT_APPLICABLE` — 이 화면에서 조작할 대상이 없다. 다시 눌러도
   * 같은 결과이므로 컨트롤 자체를 치운다(엔드포인트가 없는 구서버의 맨 404 포함).
   */
  const [notApplicable, setNotApplicable] = useState(false);
  /**
   * 같은 tick 안의 연타를 막는다. `pending` state 는 리렌더 이후에야 버튼을 비활성화
   * 하므로, 그 사이에 들어온 두 번째 호출이 반대 방향 요청을 겹쳐 보낼 수 있다.
   */
  const inFlightRef = useRef(false);

  /** 비동기 콜백에서 "지금 화면이 보고 있는 상태 응답"을 읽기 위한 거울. */
  const consentRef = useRef(consent);
  consentRef.current = consent;

  /**
   * 재조회 콜백도 거울로 읽는다 — 호출 측이 인라인 함수를 넘겨도 {@link toggle} 의
   * 정체성이 매 렌더 바뀌지 않도록.
   */
  const onSettledRef = useRef(onSettled);
  onSettledRef.current = onSettled;

  /**
   * 요청이 끝난(settle) 시점의 스냅샷. null 이면 걷을 덮개가 없다.
   *
   * `value` 가 래퍼 객체인 이유: 구서버 응답(`undefined`)도 settle 시점 값으로 담아야
   * 해서, "settle 하지 않음"과 "settle 했는데 그때 응답이 없었다"를 구분해야 한다.
   * `attempted` 는 그 요청이 만들려 한 값 — 뒤이어 도착한 서버 값이 이와 같으면 실패로
   * 보였던 요청이 실제로는 저장된 것이다.
   */
  const [settledConsent, setSettledConsent] = useState<{
    value: WaitlistPartyMarketingConsentState | undefined;
    attempted: boolean;
  } | null>(null);

  // 다른 등록(다른 파티 토큰)으로 바뀌면 이전 파티의 낙관값·안내를 물려받지 않는다.
  useEffect(() => {
    setOptimistic(null);
    setResponseCurrent(null);
    setSettledConsent(null);
    setPhase('idle');
    setNotApplicable(false);
  }, [accessToken]);

  /**
   * settle 이후 **처음 도착한 상태 응답**에서 값과 무관하게 덮개를 걷는다.
   *
   * 걷지 않으면 이후 서버에서 일어난 변화(다른 기기의 토글, 재등록 시 동의 체크로
   * 인한 팔로우 복귀)를 화면이 영영 보지 못한다. 해제 조건을 값 비교("서버가 낙관값을
   * 따라잡으면")로 두지 않는 이유는 파일 상단 주석 참고 — 커밋 뒤 타임아웃 경로에서
   * 두 값이 영원히 어긋나 덮개가 굳는다. 새 응답의 **도착**만이 신호다.
   */
  useEffect(() => {
    if (settledConsent === null || consent === settledConsent.value) {
      return;
    }
    setOptimistic(null);
    setResponseCurrent(null);
    // 서버가 시도값을 들고 왔다면 그 요청은 실패로 보였을 뿐 실제로는 저장됐다(커밋 뒤
    // 응답 유실). 오류 문구를 남기면 고객이 "재시도"로 방금 저장된 설정을 되돌린다.
    if (consent?.store_followed === settledConsent.attempted) {
      setPhase((current) => (current === 'failed' ? 'idle' : current));
    }
    setSettledConsent(null);
  }, [consent, settledConsent]);

  const followed = optimistic ?? serverFollowed === true;

  const toggle = useCallback(async () => {
    if (!accessToken || inFlightRef.current) {
      return;
    }

    const previous = followed;
    const next = !previous;

    inFlightRef.current = true;
    setOptimistic(next);
    setPhase('pending');

    try {
      const response = next
        ? await waitlistApi.followStore(accessToken)
        : await waitlistApi.unfollowStore(accessToken);

      // 서버가 확정한 값이 최종이다 — 멱등 재호출·경합 정리(23505)로 낙관값과 다를 수 있다.
      const confirmed = response?.data?.store_followed;
      setOptimistic(typeof confirmed === 'boolean' ? confirmed : next);

      // 같은 응답의 `current` 는 폴링 값보다 새롭다 — 전역 억제가 서 있으면 false 다.
      const confirmedCurrent = response?.data?.current;
      if (typeof confirmedCurrent === 'boolean') {
        setResponseCurrent(confirmedCurrent);
      }
      setPhase('idle');
    } catch (error) {
      // 무엇이 실패했든 서버는 바꾸지 않았다 — 화면도 누르기 직전으로 되돌린다.
      setOptimistic(previous);

      if (isWaitlistStoreFollowReconsentRequiredError(error)) {
        setPhase('reconsent');
        logger.warn('[waitlist] 매장 팔로우 재동의 필요(409)');
        return;
      }
      if (isWaitlistStoreFollowNotApplicableError(error)) {
        // 대상이 없다 — 오류 문구 대신 컨트롤을 치운다(고객이 할 수 있는 일이 없다).
        setNotApplicable(true);
        setPhase('idle');
        logger.warn('[waitlist] 매장 팔로우 대상 없음(404)');
        return;
      }
      setPhase('failed');
      logger.warn(
        isWaitlistStoreFollowUnavailableError(error)
          ? '[waitlist] 매장 팔로우 처리 불가(409)'
          : '[waitlist] 매장 팔로우 저장 실패',
      );
    } finally {
      inFlightRef.current = false;
      // 이 요청은 끝났다 — 다음에 도착하는 상태 응답부터 서버가 다시 권위다.
      setSettledConsent({ value: consentRef.current, attempted: next });
      // 종결 화면(seated/cancelled)은 폴링이 멈춰 있어 그 "다음 응답"이 영영 오지 않는다
      // — 여기서 한 번 요청해 덮개가 굳지 않게 한다(성공·실패 모두).
      onSettledRef.current?.();
    }
  }, [accessToken, followed]);

  /** 토글 응답이 더 새롭다 — 없을 때만 폴링 값으로 판정한다. */
  const effectiveCurrent = responseCurrent ?? serverCurrent;

  return {
    /**
     * 연락처에 현재 동의가 있고(`current`), 서버가 이 매장의 팔로우 여부를 실제로
     * 알려줬을 때만 렌더한다.
     *
     * `current === false` 면 팔로우해도 광고가 나가지 않으므로 조작면을 거둔다. 다만
     * 그 값은 실제 OFF 뿐 아니라 **판정 불가**(킬스위치 OFF·공지 계약 결손·연락처 신원
     * 모호에서의 fail-closed)까지 덮으므로, 화면은 "꺼져 있다"고 **단정하지 않고**
     * 아무 말도 하지 않는다(#885 리뷰).
     */
    visible:
      enabled && effectiveCurrent === true && typeof serverFollowed === 'boolean' && !notApplicable,
    followed,
    pending: phase === 'pending',
    failed: phase === 'failed',
    reconsentRequired: phase === 'reconsent',
    toggle,
  };
}
